import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
import { useLogger } from "../composables/useLogger";
import { isNative, getPlatform } from "~/src/utils/platform";
import { useAppTracking } from "~/composables/useAppTracking";

export default defineNuxtPlugin(async () => {
  const logger = useLogger();
  const config = useRuntimeConfig();
  const { getTrackingStatus } = useAppTracking();

  const shouldTrackInDev = true;
  const shouldTrackInAndroid = true;

  const shouldTrack = computed(async () => {
    // For web
    if (!isNative()) {
      return config.public.isDev ? shouldTrackInDev : true;
    }

    // For Android
    if (getPlatform() === "android") {
      return shouldTrackInAndroid;
    }

    // For iOS - check tracking permission
    if (getPlatform() === "ios") {
      const { status } = await getTrackingStatus();
      return status === "authorized";
    }

    return false;
  });

  // Handle Analytics Collection state
  if (isNative()) {
    try {
      const track = await shouldTrack.value;
      await FirebaseAnalytics.setCollectionEnabled({ enabled: track });
      logger.debug(
        "Firebase Analytics: Collection enabled state set to:",
        track,
      );
    } catch (error) {
      logger.logErrorCaught(
        "Firebase Analytics: Error while setting collection state",
        "firebase.client.ts",
        error,
      );
    }
  }

  // Initialize Firebase Analytics on non-android platforms that should track
  const initializeFirebaseAnalytics = async () => {
    // Skip if we're on Android since it's handled by google-services.json
    if (getPlatform() === "android") {
      logger.debug(
        "Firebase Analytics: Skipping web initialization on Android - using google-services.json configuration",
      );
      return;
    }

    const firebaseConfig = {
      apiKey: config.public.firebaseapiKey as string,
      authDomain: "nestor---tracking.firebaseapp.com",
      projectId: "nestor---tracking",
      storageBucket: "nestor---tracking.firebasestorage.app",
      messagingSenderId: "62202429101",
      appId: "1:62202429101:web:f57d854ecf8cd225a55024",
      measurementId: "G-8DLERNWE9Z",
    };

    try {
      const track = await shouldTrack.value;
      if (track) {
        await FirebaseAnalytics.initializeFirebase(firebaseConfig);
        logger.debug(
          "Firebase Analytics: Firebase Analytics initialized successfully",
        );
      } else {
        logger.debug(
          "Firebase Analytics: Firebase Analytics not initialized since tracking is not permitted",
        );
      }
    } catch (error) {
      logger.logErrorCaught(
        "Firebase Analytics: Failed to initialize Firebase Analytics",
        "firebase.client.ts",
        error,
      );
    }
  };

  // Initialize on plugin load
  await initializeFirebaseAnalytics();

  // Provide methods to track events
  const logEvent = async (eventName: string, params?: Record<string, any>) => {
    try {
      logger.debug("Firebase Analytics: Attempting to log event", {
        eventName,
        params,
      });
      const track = await shouldTrack.value;
      if (track) {
        await FirebaseAnalytics.logEvent({
          name: eventName,
          params: params || {},
        });
        logger.debug("Firebase Analytics: Event logged", { eventName, params });
      } else {
        logger.debug(
          "Firebase Analytics: Event not logged since tracking is not permitted",
          { eventName, params },
        );
      }
    } catch (error) {
      logger.logErrorCaught(
        "Firebase Analytics: Failed to log event",
        "firebase.client.ts",
        error,
        {
          eventName,
          params,
        },
      );
    }
  };

  return {
    provide: {
      firebaseAnalytics: {
        logEvent,
      },
    },
  };
});
